'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, } from 'react';
import { INPUT_BORDER_WIDTH, TEXTAREA_PAD_BLOCK, inputBase } from '../system/inputBase';
const supportsFieldSizing = () => typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content');
// Grows to fit its text (or its placeholder while empty) instead of scrolling
// inside a fixed box; `rows` is the minimum height. A caller's `maxHeight`
// caps the growth and the field scrolls past it.
export function Textarea({ rows = 4, invalid, style, ref, ...rest }) {
    const innerRef = useRef(null);
    const setRef = useCallback((el) => {
        innerRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    // Fallback for engines without `field-sizing` (iOS Safari before 26.2):
    // measure the content height on every change. Skipped while the field has
    // no width (hidden pane, background tab) — measuring there wraps the text
    // at 0px and bakes in an absurd height; the ResizeObserver re-measures
    // once it has a width again.
    useEffect(() => {
        const el = innerRef.current;
        if (!el || supportsFieldSizing())
            return;
        const grow = () => {
            if (el.clientWidth === 0)
                return;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
        };
        grow();
        el.addEventListener('input', grow);
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(grow);
        ro?.observe(el);
        return () => {
            el.removeEventListener('input', grow);
            ro?.disconnect();
        };
    }, [rest.value]);
    return (_jsx("textarea", { ref: setRef, "data-lpignore": "true", "data-1p-ignore": "true", "data-bwignore": "true", "data-form-type": "other", rows: rows, style: {
            ...inputBase,
            resize: 'vertical',
            fieldSizing: 'content',
            // `field-sizing: content` ignores `rows`, so the row count becomes a
            // border-box min-height: rows of line-height + padding + borders.
            minHeight: `calc(${rows}lh + 2 * ${TEXTAREA_PAD_BLOCK} + 2 * ${INPUT_BORDER_WIDTH})`,
            borderColor: invalid ? 'var(--destructive)' : 'var(--border-strong)',
            ...style,
        }, ...rest }));
}
//# sourceMappingURL=Textarea.js.map